import { Button, Checkbox, Select, Textarea, TextInput } from '@mantine/core';
import { type FormEvent, useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { HttpNewForm } from '@/api';
import { LOGIN_METHOD } from '@/api';
import { Tag } from '@/components/Ui/Tag';
import { useLanguageAwareRouting } from '@/hooks/useLanguageAwareRouting';
import { usePlanRetention } from '@/hooks/usePlanRetention';
import type { SiteMethod } from '@/lib/form-site';
import { STARTER_FIELDS, type StarterFieldsId } from '@/lib/form-templates';
import { useRustyState } from '@/state';
import { AddressInput } from './AddressInput';
import { createForm, defaultRecipient, starterSpecToml, useAddressPool } from './createForm';
import classes from './FormCreate.module.css';
import { MethodChoice, SITE_METHODS } from './MethodChoice';

type Encryption = 'none' | 'server' | 'nostr';
type EmailFormat = 'html' | 'text';

function ShieldIcon() {
  return (
    <svg
      className={classes.noteIcon}
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M12 3 4 6v6c0 4.5 3.4 8.3 8 9 4.6-.7 8-4.5 8-9V6z" />
      <path d="m9 12 2 2 4-4" />
    </svg>
  );
}

function XIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.2"
      strokeLinecap="round"
      aria-hidden="true"
    >
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  );
}

export function CaptchaNote({ method }: { method: SiteMethod }) {
  const { t } = useTranslation();
  return (
    <div className={classes.note}>
      <ShieldIcon />
      <span>
        <strong>{t('create.captchaOn')}</strong> {t(`create.captchaNote.${method}`)}
      </span>
    </div>
  );
}

const isStarter = (v: string | null): v is StarterFieldsId =>
  !!v && STARTER_FIELDS.some((s) => s.id === v);

export function CreateFormView() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const { createLanguageURL } = useLanguageAwareRouting();
  const [params] = useSearchParams();
  const ids = { name: useId(), method: useId(), starter: useId() };
  const session = useRustyState((s) => s.getSession());
  const hasNostrKey =
    !!session?.isLoggedIn &&
    (session.method === LOGIN_METHOD.NOSTR || session.method === LOGIN_METHOD.MANUAL) &&
    'publicKey' in session &&
    !!session.publicKey;
  const { pool, loaded } = useAddressPool();
  const retention = usePlanRetention();

  const templateParam = params.get('template');
  const methodParam = params.get('method') as SiteMethod | null;
  const [name, setName] = useState(params.get('name') ?? '');
  const [nameError, setNameError] = useState<string | null>(null);
  const [method, setMethod] = useState<SiteMethod>(
    methodParam && SITE_METHODS.includes(methodParam)
      ? methodParam
      : isStarter(templateParam)
        ? 'hosted'
        : 'own'
  );
  const [starter, setStarter] = useState<StarterFieldsId>(
    isStarter(templateParam) ? templateParam : 'contact'
  );
  const [selectedIds, setSelectedIds] = useState<string[] | null>(null);
  const [newAddresses, setNewAddresses] = useState<string[]>([]);
  const [showMore, setShowMore] = useState(false);
  const [encryption, setEncryption] = useState<Encryption>('none');
  const [thanks, setThanks] = useState('');
  const [origins, setOrigins] = useState('');
  const [keepDays, setKeepDays] = useState<number | null>(null);
  const [format, setFormat] = useState<EmailFormat>('html');
  const [spam, setSpam] = useState(false);
  const [busy, setBusy] = useState(false);

  // Pre-select the account email once the pool has loaded; after that the chips are the user's.
  useEffect(() => {
    if (!loaded || selectedIds !== null) return;
    const first = defaultRecipient(pool);
    setSelectedIds(first ? [first.id] : []);
  }, [loaded, pool, selectedIds]);

  const chosen = selectedIds ?? [];
  const hasVerified = pool.some((e) => e.is_verified);
  const chosenEmails = [
    ...pool.filter((e) => chosen.includes(e.id)).map((e) => e.email.toLowerCase()),
    ...newAddresses.map((e) => e.toLowerCase()),
  ];
  const suggestions = pool
    .filter((e) => !chosenEmails.includes(e.email.toLowerCase()))
    .map((e) => e.email);
  const days = keepDays ?? retention.max;
  const originList = origins
    .split('\n')
    .map((o) => o.trim())
    .filter(Boolean);

  const addAddress = (email: string) => {
    const lower = email.toLowerCase();
    if (chosenEmails.includes(lower)) return;
    const known = pool.find((e) => e.email.toLowerCase() === lower);
    if (known) {
      setSelectedIds([...chosen, known.id]);
    } else {
      setNewAddresses([...newAddresses, email]);
    }
  };

  const summary = [
    t(`create.enc.${encryption}`),
    thanks.trim() ? t('create.summary.thanks') : t('create.summary.noThanks'),
    originList.length > 0
      ? t('create.summary.origins', { count: originList.length })
      : t('create.summary.anySite'),
    t('create.summary.keep', { count: days }),
    spam ? t('create.summary.spamOn') : t('create.summary.spamOff'),
    t(`create.summary.format.${format}`),
  ].join(' · ');

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setNameError(t('create.nameRequired'));
      document.getElementById(ids.name)?.focus();
      return;
    }
    const body: HttpNewForm = {
      title: name.trim(),
      method,
      recipient_ids: chosen,
      check_challenge: true,
      filter_spam: spam,
      encryption_mode: encryption,
      retention_days: days,
      email_notification_format: format,
      ...(thanks.trim() ? { redirect_url: thanks.trim() } : {}),
      ...(originList.length > 0 ? { allowed_origins: originList } : {}),
      ...(method !== 'own' ? { specs: starterSpecToml(starter) } : {}),
    };
    setBusy(true);
    try {
      const form = await createForm(body, newAddresses, t);
      nav(createLanguageURL(`/account/forms/${form.id}`));
    } catch {
      // the API layer reports the error
      setBusy(false);
    }
  };

  const retentionData = [...retention.options].reverse().map((d) => ({
    value: String(d),
    label:
      d === retention.max
        ? t('create.keepPlanMax', { label: t('create.days', { count: d }) })
        : t('create.days', { count: d }),
  }));

  return (
    <form className={classes.form} onSubmit={submit} noValidate>
      <fieldset className={classes.sec}>
        <legend className={classes.legend}>
          <span className={classes.num}>1</span> {t('create.nameSection')}
        </legend>
        <div className={classes.field}>
          <label htmlFor={ids.name} className={classes.fieldLabel}>
            {t('create.nameLabel')}
          </label>
          <TextInput
            id={ids.name}
            value={name}
            onChange={(e) => {
              setName(e.currentTarget.value);
              setNameError(null);
            }}
            placeholder={t('create.namePlaceholder')}
            error={nameError}
          />
          <span className={classes.help}>{t('create.nameHelp')}</span>
        </div>
      </fieldset>

      <fieldset className={classes.sec}>
        <legend className={classes.legend}>
          <span className={classes.num}>2</span> {t('create.notifySection')}
        </legend>
        {(chosen.length > 0 || newAddresses.length > 0) && (
          <div className={classes.chips}>
            {pool
              .filter((e) => chosen.includes(e.id))
              .map((e) => (
                <span key={e.id} className={classes.chip}>
                  <span className={classes.chipEmail}>{e.email}</span>
                  {e.is_account_email && <Tag>{t('ui.accountEmail')}</Tag>}
                  {!e.is_verified && <Tag variant="pending">{t('ui.pendingVerification')}</Tag>}
                  <button
                    type="button"
                    className={classes.chipRemove}
                    aria-label={t('create.removeRecipient', { email: e.email })}
                    onClick={() => setSelectedIds(chosen.filter((id) => id !== e.id))}
                  >
                    <XIcon />
                  </button>
                </span>
              ))}
            {newAddresses.map((email) => (
              <span key={email} className={classes.chip}>
                <span className={classes.chipEmail}>{email}</span>
                <Tag variant="pending">{t('ui.pendingVerification')}</Tag>
                <button
                  type="button"
                  className={classes.chipRemove}
                  aria-label={t('create.removeRecipient', { email })}
                  onClick={() => setNewAddresses(newAddresses.filter((a) => a !== email))}
                >
                  <XIcon />
                </button>
              </span>
            ))}
          </div>
        )}
        <AddressInput
          label={
            hasVerified || !loaded ? (
              <>
                {t('create.addRecipientLabel')}{' '}
                <span className={classes.sub}>{t('create.optional')}</span>
              </>
            ) : (
              t('create.noAddressLabel')
            )
          }
          suggestions={suggestions}
          onAdd={addAddress}
          placeholder={
            hasVerified || !loaded ? t('create.recipientPlaceholder') : 'you@example.com'
          }
          help={hasVerified || !loaded ? t('create.recipientHelp') : t('create.noAddressHelp')}
          withButton={!hasVerified && loaded}
        />
        {loaded && chosen.length === 0 && newAddresses.length === 0 && (
          <span className={classes.help}>{t('create.noOneNotified')}</span>
        )}
      </fieldset>

      <fieldset className={classes.sec}>
        <legend className={classes.legend}>
          <span className={classes.num}>3</span> {t('create.whereSection')}
        </legend>
        <MethodChoice value={method} onChange={setMethod} name={ids.method} />
        {method !== 'own' && (
          <fieldset className={`${classes.plainFieldset} ${classes.starter}`}>
            <legend className={classes.quickLegend}>
              <strong>{t('create.startWith')}</strong>
            </legend>
            <div className={classes.starterList}>
              {STARTER_FIELDS.map((s) => (
                <label key={s.id} className={classes.mini}>
                  <input
                    type="radio"
                    name={ids.starter}
                    checked={starter === s.id}
                    onChange={() => setStarter(s.id)}
                  />
                  <span>{t(`create.starter.${s.id}`)}</span>
                </label>
              ))}
            </div>
            <span className={classes.help}>{t('create.startWithHelp')}</span>
          </fieldset>
        )}
        <CaptchaNote method={method} />
      </fieldset>

      <section className={classes.sec} aria-labelledby={`${ids.method}-more`}>
        <div className={classes.moreHead}>
          <div className={classes.moreTitles}>
            <h2 id={`${ids.method}-more`} className={classes.moreTitle}>
              {t('create.otherOptions')}
            </h2>
            <span className={classes.sub}>{summary}</span>
          </div>
          <Button size="xs" aria-expanded={showMore} onClick={() => setShowMore(!showMore)}>
            {showMore ? t('ui.hide') : t('create.change')}
          </Button>
        </div>
        {showMore && (
          <div className={classes.moreBody}>
            <div className={`${classes.field} ${classes.wide}`}>
              <Select
                label={t('create.encryption')}
                value={encryption}
                onChange={(v) => v && setEncryption(v as Encryption)}
                allowDeselect={false}
                data={[
                  { value: 'none', label: t('create.enc.none') },
                  { value: 'server', label: t('create.enc.server') },
                  {
                    value: 'nostr',
                    label: hasNostrKey ? t('create.enc.nostr') : t('create.encNostrDisabled'),
                    disabled: !hasNostrKey,
                  },
                ]}
              />
              <span className={classes.help}>
                <strong className={classes.strong}>{t('create.encryptionFixed')}</strong>{' '}
                {encryption === 'nostr'
                  ? t('create.encryptionNostrHelp')
                  : t('create.encryptionHelp')}
              </span>
            </div>
            <div className={classes.field}>
              <TextInput
                label={t('create.thanksLabel')}
                value={thanks}
                onChange={(e) => setThanks(e.currentTarget.value)}
                placeholder="https://example.com/thanks"
                classNames={{ input: classes.chipEmail }}
              />
              <span className={classes.help}>{t('create.thanksHelp')}</span>
            </div>
            <div className={classes.field}>
              <Textarea
                label={t('create.originsLabel')}
                value={origins}
                onChange={(e) => setOrigins(e.currentTarget.value)}
                placeholder={t('create.originsPlaceholder')}
                autosize
                minRows={1}
                maxRows={6}
                classNames={{ input: classes.chipEmail }}
              />
              <span className={classes.help}>{t('create.originsHelp')}</span>
            </div>
            <Select
              label={t('create.keepLabel')}
              value={String(days)}
              onChange={(v) => v && setKeepDays(Number(v))}
              allowDeselect={false}
              data={retentionData}
            />
            <Select
              label={t('create.formatLabel')}
              value={format}
              onChange={(v) => v && setFormat(v as EmailFormat)}
              allowDeselect={false}
              data={[
                { value: 'html', label: t('create.formatHtml') },
                { value: 'text', label: t('create.formatText') },
              ]}
            />
            <div className={`${classes.field} ${classes.wide}`}>
              <Checkbox
                label={t('create.spamLabel')}
                checked={spam}
                onChange={(e) => setSpam(e.currentTarget.checked)}
              />
              <span className={classes.help}>{t('create.spamHelp')}</span>
            </div>
          </div>
        )}
      </section>

      <div className={classes.actions}>
        <Button type="submit" variant="filled" loading={busy}>
          {t('create.submit')}
        </Button>
        <Button
          variant="default"
          onClick={() => nav(createLanguageURL('/account/forms'))}
          disabled={busy}
        >
          {t('ui.cancel')}
        </Button>
      </div>
    </form>
  );
}
